import {
  BankableTypeEnum,
  EmployerSummaryFragment,
  FormNameEnum,
  OnboardingStepType,
  useStartOnboardingStep,
} from '@alice-financial/alice-sdk'
import { List, ListItem, Typography } from '@alice-financial/pretext-ui'
import * as React from 'react'
import { makeOrgFileUploadInputDisplayConfig } from '../../../survey/questionTypes/FileUploadInputDisplay'
import { FormInputProps } from '../../../survey/types'
import { UploadBankAccounts } from '../../UploadBankAccounts'
import { useOrg } from '../../organization/useOrg'
import { useOrgFormStatus } from '../../survey/useOrgFormFields'
import { useOrgId } from '../../useOrgId'
import { EntityFunding } from './EntityFunding'
import { isPaygroupFundingConnected } from './fundingUtils'

const FORM_NAME = FormNameEnum.SecureBanking
const FORM_VERSION = 1

const baseConfig = {
  questionId: 'secureBankingFile',
  required: false,
} as const
const SECURE_FILE_UPLOAD = makeOrgFileUploadInputDisplayConfig({
  questionText: 'Payroll banking accounts file',
  questionSubtext: 'Ensure location names, account numbers, and routing numbers are clearly labeled.',
  ...baseConfig,
})
const SecureFileUpload = SECURE_FILE_UPLOAD.Input
const SECURE_FILE_UPLOAD_POST_UPLOAD = makeOrgFileUploadInputDisplayConfig({
  questionText: 'Payroll banking accounts file (processing)',
  questionSubtext: 'We will enter the payroll banking information you have supplied within 2 business days',
  ...baseConfig,
})
const SecureFileUploadPostUpload = SECURE_FILE_UPLOAD_POST_UPLOAD.Input
const QUESTIONS = [SECURE_FILE_UPLOAD] as const

const UploadBillingAccountsInputs = ({ formName, formVersion }: FormInputProps) => {
  const orgId = useOrgId()
  const formStatus = useOrgFormStatus(orgId, FORM_NAME, FORM_VERSION)
  const { mutate: startOnboardingStep } = useStartOnboardingStep(orgId)
  return formStatus === 'complete' ? (
    <SecureFileUploadPostUpload formName={formName} formVersion={formVersion} />
  ) : (
    <SecureFileUpload
      formName={formName}
      formVersion={formVersion}
      onUpload={() => startOnboardingStep({ stepType: OnboardingStepType.ConnectBanking })}
    />
  )
}

const uploadBankingAccounts = {
  Page: UploadBillingAccountsInputs,
  questions: QUESTIONS,
}
const PAGES = [uploadBankingAccounts]

const unfundedFirst = (a: EmployerSummaryFragment, b: EmployerSummaryFragment) =>
  isPaygroupFundingConnected(a) ? 1 : isPaygroupFundingConnected(b) ? -1 : 0

type PaygroupFundingListProps = {
  paygroups: Array<EmployerSummaryFragment>
  readonly?: boolean
}
/**
 * A list of companies that need to finish connecting payment - i.e. companies with
 * alice_card_funding_status of 'not_connected' or 'pending_verification'
 *
 * This list should always display on the org dashboard in the non-unified banking case, and
 * never display on the org dashboard in the unified banking case.
 *
 * It should only display on the employer dashboard if the employer has already connected banking, i.e.
 * it should not display if the admin still needs to finish connecting the current employer
 *
 */
export const PaygroupFundingList = ({ paygroups, readonly }: PaygroupFundingListProps) => {
  const org = useOrg()
  if (!org) return null
  if (paygroups.length === 0) return null

  return (
    <>
      <Typography variant="h3" mt={2} gutterBottom>
        Pay group funding accounts
      </Typography>
      <UploadBankAccounts
        org={org}
        formName={FORM_NAME}
        formVersion={FORM_VERSION}
        pages={PAGES}
        onCompleteReturnTo="billing"
        type="billing"
      />

      <List sx={{ width: '100%' }}>
        {paygroups.sort(unfundedFirst).map((paygroup) => (
          <ListItem key={paygroup.id} divider>
            <EntityFunding entity={{ type: BankableTypeEnum.Paygroup, ...paygroup }} readonly={readonly} />
          </ListItem>
        ))}
      </List>
    </>
  )
}
